<script setup>
import { titleCase } from '@/@core/utils/formatters'
import ticketService from '@/services/service-desk/ticket.service'
import GoogleTranslateSelect from '@google-translate-select/vue3'
import { themeConfig } from '@themeConfig'
import { ref, watch } from 'vue'

definePage({ 
  meta: { 
    layout: 'client', 
    public: true, 
    unauthenticatedOnly: true,
    title: 'Write Complaint',
  }, 
})


const router = useRouter()
const route = useRoute()

// we need to take source type form type and episode number from the url
const { s: sourceType,  e: episodeNumber, l: locationNo, u: unitValue } = route.query
const episodeNoValue =ref(null)

const location = ref(null)
const unitId = ref(null)
const sourceTypeValue = ref(null)
const contactForm = ref()
try{
  episodeNoValue.value = episodeNumber ? atob(episodeNumber) : null
  location.value = locationNo ? atob(locationNo) : null
  sourceTypeValue.value = sourceType ? atob(sourceType) : null
  unitId.value = unitValue ? atob(unitValue) : 1
}
catch(error){
  console.error('Error parsing URL:', error)
  router.push({ name: 'invalid-url', params: { code: 404,
    message: 'Invalid URL. Please try again with a valid URL.',
  
  } })
}

const serviceData = reactive({
  customer_id: '',
  customer_name: '',
  ticket_type: 'COMPLAINT',
  reporter_type: 'PATIENT',
  customer_email: '',
  location_id: null,
  customer_phone: '',
  customer_uhid: '',
  episode_no: '',
  consent: false,
  unit_id: null,
  department_id: null,
  description: '',
})

// Categories
const {
  data: categoriesData,
  execute: fetchCategories,
} = useApi(createUrl('/service-desk/public/categories', {
  query: {
    search: {
      parent_id: 'not-null',
    },
  },
}))

const selectedCategory = ref()

watch(() => selectedCategory.value, value => {
  serviceData.category_id = value?.parent_id
  serviceData.child_category_id = value?.id
  serviceData.department_id = value?.department_id
  serviceData.ticket_type = value?.ticket_type
})

// if source type is not present in the url then throw error
if (!sourceTypeValue.value) {
  router.push({ name: 'invalid-url', params: { code: 404 } })
}else{
  await useApi(createUrl('public/feedback-sources', 
    { 
      query: { 
        search: {
          status: 1,
          id: sourceTypeValue,
        },
      },
    },
  ), {
    afterFetch: response => {
      if (response?.data?.length=== 0) {
        router.push({ name: 'invalid-url', params: { code: 404 } })
      }
      serviceData.sourceType= response.data[0]?.id

    },
  })
}

const unit = ref(null)

if(unitId.value){
  await useApi(createUrl('public/units', 
    { 
      query: { 
        search: {
          id: unitId.value??1,
        },
      },
    },
  ), {
    afterFetch: response => {
      if (response?.data?.length === 0) {
        router.push({ name: 'invalid-url', params: { code: 404 } })
      }
      unit.value = response.data[0]
      serviceData.unit_id = unitId.value
    },
  })
}

// if episode number is present validate it
if (episodeNoValue.value) {
  serviceData.episode_id = episodeNoValue
}
if(location.value) {
  serviceData.location_id = location.value
  await useApi(createUrl('service-desk/public/location-wise-customer', 
    { 
      query: { 
        unit_id: unitId.value??1,
        location_id: location.value,
      },
    },
  ), {
    afterFetch: response => {
      if (response?.data?.length === 0) {
        return
      }
      serviceData.customer_id = response.data.id
      serviceData.customer_name = response.data.name
      serviceData.customer_phone = response.data.phone
      serviceData.customer_uhid = response.data.uhid
    },
  })
}else{
  router.push({ name: 'invalid-url', params: { code: 404 } })
}

const currentStep = ref(0)

const checkCurrentStep = computed(() => {
  if (currentStep.value === 0) {
    return serviceData.consent
  }
  if (currentStep.value === 1) {
    return serviceData.description && serviceData.department_id
  }

  return true
})

const buttonLabel = computed(() => {
  return currentStep.value === 1 ? 'Submit Complaint' : 'Raise Complaint'
})

const submitBtnLoading = ref(false)


const nextStep = () => {
  switch (currentStep.value) {
  case 0:
    contactForm.value?.validate().then(({ valid: isValid }) => {
      if (isValid) {
        currentStep.value += 1
      }
    })

    break

  case 1:
    submitBtnLoading.value = true
    ticketService.createPublicTickets(serviceData, null,  null, () => {
      submitBtnLoading.value = true
    }).then(() => {
      submitBtnLoading.value = false
      currentStep.value += 1 
    }).catch(() => {
      submitBtnLoading.value = false
    })

    break
    
  default:
    currentStep.value += 1
  }
}

const handleError = error => {
  console.error('Translation error:', error)
}

const handleChange = lang => { 
  if (!lang) return
  
  const googleFrame = document.querySelector('.goog-te-combo')
  if (googleFrame) {
    googleFrame.value = lang.code
    googleFrame.dispatchEvent(new Event('change'))
  }
}

const previousStep = () => {
  currentStep.value = currentStep.value - 1
}

const googleTranslateSelect =ref()

const languages = ref([
  {
    code: 'en',
    L: 'en',
    name: 'English',
    cname: '英语',
    ename: 'English',
  },
  {
    code: 'hi',
    L: 'hi',
    name: 'हिंदी',
    cname: '印地语',
    ename: 'Hindi',
  },
  {
    code: 'ml',
    L: 'ml',
    name: 'മലയാളം',
    cname: '马拉雅拉姆语',
    ename: 'Malayalam',
  },
  {
    code: 'kn',
    L: 'kn',
    name: 'ಕನ್ನಡ',
    cname: '卡纳达语',
    ename: 'Kannada',
  },
])

// const { data: departments } =await useApi(createUrl('/masters/departments', {
//   query: {
//     search: {
//       show_on_service_desk: true,
//     },
//   },
// }))


const notYouDisabled = ref(false)

const resetServiceData = () => {
  serviceData.customer_id = ''
  serviceData.customer_name = ''
  serviceData.customer_email = ''
  serviceData.customer_phone = ''
  serviceData.customer_uhid = ''

  notYouDisabled.value = true
}


const isChecked = departmentId => {
  return serviceData.department_id === departmentId
}

const onCheckboxUpdate = (checkboxValue, departmentId) => {
  if (!checkboxValue) {
    serviceData.department_id = departmentId
  } else {
    serviceData.department_id = null
  }
}
</script>

<template>
  <div class="feedback-card feedback-container mx-auto pt-6 pt-sm-10">
    <VCard variant="flat">
      <VWindow
        v-model="currentStep"
        class="disable-tab-transition"
        :touch="false"
      >
        <!-- Step 1: Contact Information -->
        <VWindowItem>
          <VCardText class="px-4 px-sm-6">
            <div
              v-if="languages && languages?.length"
              class="translate-wrapper"
            >
              <GoogleTranslateSelect
                ref="googleTranslateSelect"
                default-language-code="en"
                default-page-language-code="en"
                :fetch-browser-language="false"
                trigger="click"
                class="border rounded"
                :languages="languages"
                @error="handleError"
                @change="handleChange"
              />
            </div>
          </VCardText>

          <VCardText class="px-4 px-sm-6">
            <div class="text-center text-primary text-h5 mb-5">
              Welcome to {{ titleCase(unit?.display_name ?? themeConfig.app.client.name) }} - Service Desk
            </div>

            <VForm ref="contactForm">
              <VRow>
                <VCol
                  v-if="serviceData.customer_id"
                  cols="12"
                >
                  <VAlert
                    variant="tonal"
                    color="grey-400"
                  >
                    <VBtn
                      variant="text"
                      color="primary"
                      :disabled="notYouDisabled"
                      @click="resetServiceData"
                    >
                      Not You? Click Here
                    </VBtn>
                  </VAlert>
                </VCol>

                <VCol
                  cols="12"
                  md="12"
                >
                  <AppTextField
                    v-model="serviceData.customer_name"
                    label="Name"
                    placeholder="Enter Name"
                    required
                    :rules="[requiredValidator]"
                  />
                </VCol>

                <VCol
                  cols="12"
                  md="12"
                >
                  <AppPhoneInput
                    v-model="serviceData.customer_phone"
                    label="Mobile Number"
                    placeholder="Enter Mobile Number"
                    :rules="[requiredValidator]"
                    required
                  />
                </VCol>

                <VCol
                  cols="12"
                  md="12"
                >
                  <AppTextField
                    v-model="serviceData.customer_uhid"
                    label="UHID No"
                    placeholder="Enter UHID"
                  />
                </VCol>

                <VCol
                  cols="12"
                  md="12"
                >
                  <AppTextField
                    v-model="serviceData.customer_email"
                    label="Email"
                    type="email"
                    placeholder="Enter Email"
                    :rules="[emailValidator]"
                  />
                </VCol>
              </VRow>
            </VForm>

            <VAlert
              variant="tonal"
              color="grey-400"
              class="mt-4"
            >
              <div class="d-flex align-start">
                <VCheckbox
                  v-model="serviceData.consent"
                  color="primary"
                >
                  <template #label>
                    <span>
                      I hereby authorize {{ unit?.display_name }} to use this information for public relation effort or for any other lawful purpose.
                      <a
                        v-if="unit?.privacy_policy"
                        :href="unit?.privacy_policy"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Privacy Policy
                      </a>
                    </span>
                  </template>
                </VCheckbox>
              </div>
            </VAlert>
          </VCardText>
        </VWindowItem>

        <!-- Step 2: Task & Description -->
        <VWindowItem>
          <VCardText class="px-4 px-sm-6">
            <VRow>
              <VCol
                cols="12"
                md="12"
              >
                <AppCombobox
                  v-model="selectedCategory"
                  :items="categoriesData ?? []"
                  :item-title="item => item.name"
                  :item-value="item => item.id"
                  label="Task"
                  placeholder="Select Task"
                  :hide-no-data="false"
                  :multiple="false"
                  :return-object="true"
                  :clearable="true"
                  required
                />
              </VCol>

              <VCol
                cols="12"
                md="12"
              >
                <AppTextarea
                  v-model="serviceData.description"
                  rows="3"
                  label="Description"
                  placeholder="Write Description"
                  :rules="[requiredValidator]"
                  required
                />
              </VCol>
            </VRow>
          </VCardText>
        </VWindowItem>

        <!-- Step 3: Success -->
        <VWindowItem>
          <VCardText class="px-4 px-sm-6">
            <div class="text-center pa-10">
              <VIcon
                color="success"
                size="64"
                icon="tabler-circle-dashed-check"
              />
              <h2 class="text-h5 mt-4">
                Thank You!
              </h2>
              <p class="text-body-1 mt-2">
                Your Complaint has been successfully submitted.
              </p>
            </div>
          </VCardText>
        </VWindowItem>
      </VWindow>

      <!-- Navigation Buttons -->
      <VCardText
        v-if="currentStep < 2"
        class="px-4 px-sm-6 d-flex"
        :class="{
          'justify-center': currentStep === 0,
          'justify-space-between': currentStep !== 0,
        }"
      >
        <VBtn
          v-show="currentStep !== 0"
          variant="tonal"
          prepend-icon="tabler-arrow-left"
          @click="previousStep"
        >
          Back
        </VBtn>
        <VBtn
          color="primary"
          variant="flat"
          :append-icon="currentStep === 0 ? 'tabler-arrow-right' : undefined"
          :disabled="!checkCurrentStep"
          :loading="submitBtnLoading"
          :block="currentStep === 0"
          @click="nextStep"
        >
          {{ buttonLabel }}
        </VBtn>
      </VCardText>
    </VCard>
  </div>
</template>

<style lang="scss">
.feedback-card {
  margin-block: 6rem;
}

@media (max-width: 600px){
  .feedback-card {
    margin-block-start: 6rem;
  }
}

.feedback-container{
  max-inline-size: 42rem;
}

@media (max-width: 1024px){
  .feedback-card {
    max-inline-size: 100%;
  }
}

.translate-wrapper {
  inline-size: 100%;
}

.google-translate-select-flag {
  .google-translate-select-flag__mr{
    background-image: url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHhtbG5zOnhsaW5rPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5L3hsaW5rIiBpZD0iZmxhZy1pY29ucy1pbiIgdmlld0JveD0iMCAwIDY0MCA0ODAiPgogIDxwYXRoIGZpbGw9IiNmOTMiIGQ9Ik0wIDBoNjQwdjE2MEgweiIvPgogIDxwYXRoIGZpbGw9IiNmZmYiIGQ9Ik0wIDE2MGg2NDB2MTYwSDB6Ii8+CiAgPHBhdGggZmlsbD0iIzEyODgwNyIgZD0iTTAgMzIwaDY0MHYxNjBIMHoiLz4KICA8ZyB0cmFuc2Zvcm09Im1hdHJpeCgzLjIgMCAwIDMuMiAzMjAgMjQwKSI+CiAgICA8Y2lyY2xlIHI9IjIwIiBmaWxsPSIjMDA4Ii8+CiAgICA8Y2lyY2xlIHI9IjE3LjUiIGZpbGw9IiNmZmYiLz4KICAgIDxjaXJjbGUgcj0iMy41IiBmaWxsPSIjMDA4Ii8+CiAgICA8ZyBpZD0iaW4tZCI+CiAgICAgIDxnIGlkPSJpbi1jIj4KICAgICAgICA8ZyBpZD0iaW4tYiI+CiAgICAgICAgICA8ZyBpZD0iaW4tYSIgZmlsbD0iIzAwOCI+CiAgICAgICAgICAgIDxjaXJjbGUgcj0iLjkiIHRyYW5zZm9ybT0icm90YXRlKDcuNSAtOC44IDEzMy41KSIvPgogICAgICAgICAgICA8cGF0aCBkPSJNMCAxNy41LjYgNyAwIDJsLS42IDV6Ii8+CiAgICAgICAgICA8L2c+CiAgICAgICAgICA8dXNlIHhsaW5rOmhyZWY9IiNpbi1hIiB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiB0cmFuc2Zvcm09InJvdGF0ZSgxNSkiLz4KICAgICAgICA8L2c+CiAgICAgICAgPHVzZSB4bGluazpocmVmPSIjaW4tYiIgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgdHJhbnNmb3JtPSJyb3RhdGUoMzApIi8+CiAgICAgIDwvZz4KICAgICAgPHVzZSB4bGluazpocmVmPSIjaW4tYyIgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgdHJhbnNmb3JtPSJyb3RhdGUoNjApIi8+CiAgICA8L2c+CiAgICA8dXNlIHhsaW5rOmhyZWY9IiNpbi1kIiB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiB0cmFuc2Zvcm09InJvdGF0ZSgxMjApIi8+CiAgICA8dXNlIHhsaW5rOmhyZWY9IiNpbi1kIiB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiB0cmFuc2Zvcm09InJvdGF0ZSgtMTIwKSIvPgogIDwvZz4KPC9zdmc+');
    background-size: cover;
    block-size: 100%;
    inline-size: 100%;

  }

  .google-translate-select-flag__bn{
    background-image: url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHhtbG5zOnhsaW5rPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5L3hsaW5rIiBpZD0iZmxhZy1pY29ucy1pbiIgdmlld0JveD0iMCAwIDY0MCA0ODAiPgogIDxwYXRoIGZpbGw9IiNmOTMiIGQ9Ik0wIDBoNjQwdjE2MEgweiIvPgogIDxwYXRoIGZpbGw9IiNmZmYiIGQ9Ik0wIDE2MGg2NDB2MTYwSDB6Ii8+CiAgPHBhdGggZmlsbD0iIzEyODgwNyIgZD0iTTAgMzIwaDY0MHYxNjBIMHoiLz4KICA8ZyB0cmFuc2Zvcm09Im1hdHJpeCgzLjIgMCAwIDMuMiAzMjAgMjQwKSI+CiAgICA8Y2lyY2xlIHI9IjIwIiBmaWxsPSIjMDA4Ii8+CiAgICA8Y2lyY2xlIHI9IjE3LjUiIGZpbGw9IiNmZmYiLz4KICAgIDxjaXJjbGUgcj0iMy41IiBmaWxsPSIjMDA4Ii8+CiAgICA8ZyBpZD0iaW4tZCI+CiAgICAgIDxnIGlkPSJpbi1jIj4KICAgICAgICA8ZyBpZD0iaW4tYiI+CiAgICAgICAgICA8ZyBpZD0iaW4tYSIgZmlsbD0iIzAwOCI+CiAgICAgICAgICAgIDxjaXJjbGUgcj0iLjkiIHRyYW5zZm9ybT0icm90YXRlKDcuNSAtOC44IDEzMy41KSIvPgogICAgICAgICAgICA8cGF0aCBkPSJNMCAxNy41LjYgNyAwIDJsLS42IDV6Ii8+CiAgICAgICAgICA8L2c+CiAgICAgICAgICA8dXNlIHhsaW5rOmhyZWY9IiNpbi1hIiB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiB0cmFuc2Zvcm09InJvdGF0ZSgxNSkiLz4KICAgICAgICA8L2c+CiAgICAgICAgPHVzZSB4bGluazpocmVmPSIjaW4tYiIgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgdHJhbnNmb3JtPSJyb3RhdGUoMzApIi8+CiAgICAgIDwvZz4KICAgICAgPHVzZSB4bGluazpocmVmPSIjaW4tYyIgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgdHJhbnNmb3JtPSJyb3RhdGUoNjApIi8+CiAgICA8L2c+CiAgICA8dXNlIHhsaW5rOmhyZWY9IiNpbi1kIiB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiB0cmFuc2Zvcm09InJvdGF0ZSgxMjApIi8+CiAgICA8dXNlIHhsaW5rOmhyZWY9IiNpbi1kIiB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiB0cmFuc2Zvcm09InJvdGF0ZSgtMTIwKSIvPgogIDwvZz4KPC9zdmc+');
    background-size: cover;
    block-size: 100%;
    inline-size: 100%;

  }

  .google-translate-select-flag__ta{
    background-image: url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHhtbG5zOnhsaW5rPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5L3hsaW5rIiBpZD0iZmxhZy1pY29ucy1pbiIgdmlld0JveD0iMCAwIDY0MCA0ODAiPgogIDxwYXRoIGZpbGw9IiNmOTMiIGQ9Ik0wIDBoNjQwdjE2MEgweiIvPgogIDxwYXRoIGZpbGw9IiNmZmYiIGQ9Ik0wIDE2MGg2NDB2MTYwSDB6Ii8+CiAgPHBhdGggZmlsbD0iIzEyODgwNyIgZD0iTTAgMzIwaDY0MHYxNjBIMHoiLz4KICA8ZyB0cmFuc2Zvcm09Im1hdHJpeCgzLjIgMCAwIDMuMiAzMjAgMjQwKSI+CiAgICA8Y2lyY2xlIHI9IjIwIiBmaWxsPSIjMDA4Ii8+CiAgICA8Y2lyY2xlIHI9IjE3LjUiIGZpbGw9IiNmZmYiLz4KICAgIDxjaXJjbGUgcj0iMy41IiBmaWxsPSIjMDA4Ii8+CiAgICA8ZyBpZD0iaW4tZCI+CiAgICAgIDxnIGlkPSJpbi1jIj4KICAgICAgICA8ZyBpZD0iaW4tYiI+CiAgICAgICAgICA8ZyBpZD0iaW4tYSIgZmlsbD0iIzAwOCI+CiAgICAgICAgICAgIDxjaXJjbGUgcj0iLjkiIHRyYW5zZm9ybT0icm90YXRlKDcuNSAtOC44IDEzMy41KSIvPgogICAgICAgICAgICA8cGF0aCBkPSJNMCAxNy41LjYgNyAwIDJsLS42IDV6Ii8+CiAgICAgICAgICA8L2c+CiAgICAgICAgICA8dXNlIHhsaW5rOmhyZWY9IiNpbi1hIiB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiB0cmFuc2Zvcm09InJvdGF0ZSgxNSkiLz4KICAgICAgICA8L2c+CiAgICAgICAgPHVzZSB4bGluazpocmVmPSIjaW4tYiIgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgdHJhbnNmb3JtPSJyb3RhdGUoMzApIi8+CiAgICAgIDwvZz4KICAgICAgPHVzZSB4bGluazpocmVmPSIjaW4tYyIgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgdHJhbnNmb3JtPSJyb3RhdGUoNjApIi8+CiAgICA8L2c+CiAgICA8dXNlIHhsaW5rOmhyZWY9IiNpbi1kIiB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiB0cmFuc2Zvcm09InJvdGF0ZSgxMjApIi8+CiAgICA8dXNlIHhsaW5rOmhyZWY9IiNpbi1kIiB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiB0cmFuc2Zvcm09InJvdGF0ZSgtMTIwKSIvPgogIDwvZz4KPC9zdmc+');
    background-size: cover;
    block-size: 100%;
    inline-size: 100%;

  }

  .google-translate-select-flag__te{
    background-image: url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHhtbG5zOnhsaW5rPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5L3hsaW5rIiBpZD0iZmxhZy1pY29ucy1pbiIgdmlld0JveD0iMCAwIDY0MCA0ODAiPgogIDxwYXRoIGZpbGw9IiNmOTMiIGQ9Ik0wIDBoNjQwdjE2MEgweiIvPgogIDxwYXRoIGZpbGw9IiNmZmYiIGQ9Ik0wIDE2MGg2NDB2MTYwSDB6Ii8+CiAgPHBhdGggZmlsbD0iIzEyODgwNyIgZD0iTTAgMzIwaDY0MHYxNjBIMHoiLz4KICA8ZyB0cmFuc2Zvcm09Im1hdHJpeCgzLjIgMCAwIDMuMiAzMjAgMjQwKSI+CiAgICA8Y2lyY2xlIHI9IjIwIiBmaWxsPSIjMDA4Ii8+CiAgICA8Y2lyY2xlIHI9IjE3LjUiIGZpbGw9IiNmZmYiLz4KICAgIDxjaXJjbGUgcj0iMy41IiBmaWxsPSIjMDA4Ii8+CiAgICA8ZyBpZD0iaW4tZCI+CiAgICAgIDxnIGlkPSJpbi1jIj4KICAgICAgICA8ZyBpZD0iaW4tYiI+CiAgICAgICAgICA8ZyBpZD0iaW4tYSIgZmlsbD0iIzAwOCI+CiAgICAgICAgICAgIDxjaXJjbGUgcj0iLjkiIHRyYW5zZm9ybT0icm90YXRlKDcuNSAtOC44IDEzMy41KSIvPgogICAgICAgICAgICA8cGF0aCBkPSJNMCAxNy41LjYgNyAwIDJsLS42IDV6Ii8+CiAgICAgICAgICA8L2c+CiAgICAgICAgICA8dXNlIHhsaW5rOmhyZWY9IiNpbi1hIiB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiB0cmFuc2Zvcm09InJvdGF0ZSgxNSkiLz4KICAgICAgICA8L2c+CiAgICAgICAgPHVzZSB4bGluazpocmVmPSIjaW4tYiIgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgdHJhbnNmb3JtPSJyb3RhdGUoMzApIi8+CiAgICAgIDwvZz4KICAgICAgPHVzZSB4bGluazpocmVmPSIjaW4tYyIgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgdHJhbnNmb3JtPSJyb3RhdGUoNjApIi8+CiAgICA8L2c+CiAgICA8dXNlIHhsaW5rOmhyZWY9IiNpbi1kIiB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiB0cmFuc2Zvcm09InJvdGF0ZSgxMjApIi8+CiAgICA8dXNlIHhsaW5rOmhyZWY9IiNpbi1kIiB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiB0cmFuc2Zvcm09InJvdGF0ZSgtMTIwKSIvPgogIDwvZz4KPC9zdmc+');
    background-size: cover;
    block-size: 100%;
    inline-size: 100%;

  }

  .google-translate-select-flag__kn{
    background-image: url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHhtbG5zOnhsaW5rPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5L3hsaW5rIiBpZD0iZmxhZy1pY29ucy1pbiIgdmlld0JveD0iMCAwIDY0MCA0ODAiPgogIDxwYXRoIGZpbGw9IiNmOTMiIGQ9Ik0wIDBoNjQwdjE2MEgweiIvPgogIDxwYXRoIGZpbGw9IiNmZmYiIGQ9Ik0wIDE2MGg2NDB2MTYwSDB6Ii8+CiAgPHBhdGggZmlsbD0iIzEyODgwNyIgZD0iTTAgMzIwaDY0MHYxNjBIMHoiLz4KICA8ZyB0cmFuc2Zvcm09Im1hdHJpeCgzLjIgMCAwIDMuMiAzMjAgMjQwKSI+CiAgICA8Y2lyY2xlIHI9IjIwIiBmaWxsPSIjMDA4Ii8+CiAgICA8Y2lyY2xlIHI9IjE3LjUiIGZpbGw9IiNmZmYiLz4KICAgIDxjaXJjbGUgcj0iMy41IiBmaWxsPSIjMDA4Ii8+CiAgICA8ZyBpZD0iaW4tZCI+CiAgICAgIDxnIGlkPSJpbi1jIj4KICAgICAgICA8ZyBpZD0iaW4tYiI+CiAgICAgICAgICA8ZyBpZD0iaW4tYSIgZmlsbD0iIzAwOCI+CiAgICAgICAgICAgIDxjaXJjbGUgcj0iLjkiIHRyYW5zZm9ybT0icm90YXRlKDcuNSAtOC44IDEzMy41KSIvPgogICAgICAgICAgICA8cGF0aCBkPSJNMCAxNy41LjYgNyAwIDJsLS42IDV6Ii8+CiAgICAgICAgICA8L2c+CiAgICAgICAgICA8dXNlIHhsaW5rOmhyZWY9IiNpbi1hIiB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiB0cmFuc2Zvcm09InJvdGF0ZSgxNSkiLz4KICAgICAgICA8L2c+CiAgICAgICAgPHVzZSB4bGluazpocmVmPSIjaW4tYiIgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgdHJhbnNmb3JtPSJyb3RhdGUoMzApIi8+CiAgICAgIDwvZz4KICAgICAgPHVzZSB4bGluazpocmVmPSIjaW4tYyIgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgdHJhbnNmb3JtPSJyb3RhdGUoNjApIi8+CiAgICA8L2c+CiAgICA8dXNlIHhsaW5rOmhyZWY9IiNpbi1kIiB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiB0cmFuc2Zvcm09InJvdGF0ZSgxMjApIi8+CiAgICA8dXNlIHhsaW5rOmhyZWY9IiNpbi1kIiB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiB0cmFuc2Zvcm09InJvdGF0ZSgtMTIwKSIvPgogIDwvZz4KPC9zdmc+');
    background-size: cover;
    block-size: 100%;
    inline-size: 100%;

  }

  .google-translate-select-flag__ml{
    background-image: url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHhtbG5zOnhsaW5rPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5L3hsaW5rIiBpZD0iZmxhZy1pY29ucy1pbiIgdmlld0JveD0iMCAwIDY0MCA0ODAiPgogIDxwYXRoIGZpbGw9IiNmOTMiIGQ9Ik0wIDBoNjQwdjE2MEgweiIvPgogIDxwYXRoIGZpbGw9IiNmZmYiIGQ9Ik0wIDE2MGg2NDB2MTYwSDB6Ii8+CiAgPHBhdGggZmlsbD0iIzEyODgwNyIgZD0iTTAgMzIwaDY0MHYxNjBIMHoiLz4KICA8ZyB0cmFuc2Zvcm09Im1hdHJpeCgzLjIgMCAwIDMuMiAzMjAgMjQwKSI+CiAgICA8Y2lyY2xlIHI9IjIwIiBmaWxsPSIjMDA4Ii8+CiAgICA8Y2lyY2xlIHI9IjE3LjUiIGZpbGw9IiNmZmYiLz4KICAgIDxjaXJjbGUgcj0iMy41IiBmaWxsPSIjMDA4Ii8+CiAgICA8ZyBpZD0iaW4tZCI+CiAgICAgIDxnIGlkPSJpbi1jIj4KICAgICAgICA8ZyBpZD0iaW4tYiI+CiAgICAgICAgICA8ZyBpZD0iaW4tYSIgZmlsbD0iIzAwOCI+CiAgICAgICAgICAgIDxjaXJjbGUgcj0iLjkiIHRyYW5zZm9ybT0icm90YXRlKDcuNSAtOC44IDEzMy41KSIvPgogICAgICAgICAgICA8cGF0aCBkPSJNMCAxNy41LjYgNyAwIDJsLS42IDV6Ii8+CiAgICAgICAgICA8L2c+CiAgICAgICAgICA8dXNlIHhsaW5rOmhyZWY9IiNpbi1hIiB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiB0cmFuc2Zvcm09InJvdGF0ZSgxNSkiLz4KICAgICAgICA8L2c+CiAgICAgICAgPHVzZSB4bGluazpocmVmPSIjaW4tYiIgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgdHJhbnNmb3JtPSJyb3RhdGUoMzApIi8+CiAgICAgIDwvZz4KICAgICAgPHVzZSB4bGluazpocmVmPSIjaW4tYyIgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgdHJhbnNmb3JtPSJyb3RhdGUoNjApIi8+CiAgICA8L2c+CiAgICA8dXNlIHhsaW5rOmhyZWY9IiNpbi1kIiB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiB0cmFuc2Zvcm09InJvdGF0ZSgxMjApIi8+CiAgICA8dXNlIHhsaW5rOmhyZWY9IiNpbi1kIiB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiB0cmFuc2Zvcm09InJvdGF0ZSgtMTIwKSIvPgogIDwvZz4KPC9zdmc+');
    background-size: cover;
    block-size: 100%;
    inline-size: 100%;

  }

  .google-translate-select-flag__pa{
    background-image: url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHhtbG5zOnhsaW5rPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5L3hsaW5rIiBpZD0iZmxhZy1pY29ucy1pbiIgdmlld0JveD0iMCAwIDY0MCA0ODAiPgogIDxwYXRoIGZpbGw9IiNmOTMiIGQ9Ik0wIDBoNjQwdjE2MEgweiIvPgogIDxwYXRoIGZpbGw9IiNmZmYiIGQ9Ik0wIDE2MGg2NDB2MTYwSDB6Ii8+CiAgPHBhdGggZmlsbD0iIzEyODgwNyIgZD0iTTAgMzIwaDY0MHYxNjBIMHoiLz4KICA8ZyB0cmFuc2Zvcm09Im1hdHJpeCgzLjIgMCAwIDMuMiAzMjAgMjQwKSI+CiAgICA8Y2lyY2xlIHI9IjIwIiBmaWxsPSIjMDA4Ii8+CiAgICA8Y2lyY2xlIHI9IjE3LjUiIGZpbGw9IiNmZmYiLz4KICAgIDxjaXJjbGUgcj0iMy41IiBmaWxsPSIjMDA4Ii8+CiAgICA8ZyBpZD0iaW4tZCI+CiAgICAgIDxnIGlkPSJpbi1jIj4KICAgICAgICA8ZyBpZD0iaW4tYiI+CiAgICAgICAgICA8ZyBpZD0iaW4tYSIgZmlsbD0iIzAwOCI+CiAgICAgICAgICAgIDxjaXJjbGUgcj0iLjkiIHRyYW5zZm9ybT0icm90YXRlKDcuNSAtOC44IDEzMy41KSIvPgogICAgICAgICAgICA8cGF0aCBkPSJNMCAxNy41LjYgNyAwIDJsLS42IDV6Ii8+CiAgICAgICAgICA8L2c+CiAgICAgICAgICA8dXNlIHhsaW5rOmhyZWY9IiNpbi1hIiB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiB0cmFuc2Zvcm09InJvdGF0ZSgxNSkiLz4KICAgICAgICA8L2c+CiAgICAgICAgPHVzZSB4bGluazpocmVmPSIjaW4tYiIgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgdHJhbnNmb3JtPSJyb3RhdGUoMzApIi8+CiAgICAgIDwvZz4KICAgICAgPHVzZSB4bGluazpocmVmPSIjaW4tYyIgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgdHJhbnNmb3JtPSJyb3RhdGUoNjApIi8+CiAgICA8L2c+CiAgICA8dXNlIHhsaW5rOmhyZWY9IiNpbi1kIiB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiB0cmFuc2Zvcm09InJvdGF0ZSgxMjApIi8+CiAgICA8dXNlIHhsaW5rOmhyZWY9IiNpbi1kIiB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiB0cmFuc2Zvcm09InJvdGF0ZSgtMTIwKSIvPgogIDwvZz4KPC9zdmc+');
    background-size: cover;
    block-size: 100%;
    inline-size: 100%;

  }
}
</style>
